'use client';

/**
 * ObeyaPlanLensFirmnessOpenCard — every open requirement (a known unknown) with the candidates that could fill it.
 * A renderer: every count, mark and range arrives computed from the BFF.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensFirmnessOpenCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanLenses, money, TIER_COLOR } from './use-plan-lenses';

export function ObeyaPlanLensFirmnessOpenCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_firmness_open',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.open_requirements : 'obeya_plan_lens_firmness_open',
    json: d ? d.knowledge : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const k = d.knowledge.data;

  return (
    <div className="flex flex-col h-full p-6 gap-3 overflow-auto">
      <div>
        <div className={cn(text.eyebrow, 'mb-2')}>{L.open_requirements} · {k.open_count}</div>
        <div className={cn(text.muted, 'flex gap-3 pb-1 border-b border-border')}>
          <span className="w-[22%]">{L.col_requirement}</span>
          <span className="flex-1">{L.col_rooms}</span>
          <span className="w-[7%] text-right">{L.col_qty}</span>
          <span className="w-[14%]">{L.col_candidates}</span>
          <span className="w-[8%] text-right">{L.col_rfq}</span>
          <span className="w-[8%] text-right">{L.col_quoted}</span>
          <span className="w-[17%] text-right">{L.col_price_range}</span>
        </div>
        {k.open.map((r) => (
          <div key={r.requirement_id} className={cn(text.body, 'flex gap-3 py-1 items-center')}>
            <span className="w-[22%] truncate">{r.requirement ?? L.unlabelled}</span>
            <span className={cn(text.muted, 'flex-1 truncate')}>{r.rooms.join(', ')}</span>
            <span className={cn(text.muted, 'w-[7%] text-right tabular-nums')}>{r.quantity ?? L.mark_none}</span>
            <span className="w-[14%] flex items-center gap-2">
              <span className={cn(text.muted, 'tabular-nums')}>{r.candidate_count}</span>
              {r.candidate_tiers.map((x) => (
                <span key={x.tier} className={cn(text.muted, 'inline-flex items-center gap-1 tabular-nums')}>
                  <span className="inline-block w-[7px] h-[7px] rounded-full" style={{ background: TIER_COLOR[x.tier] }} />{x.count}
                </span>
              ))}
            </span>
            <span className={cn(text.muted, 'w-[8%] text-right tabular-nums')}>{r.candidates_with_sent_rfq}/{r.candidate_count}</span>
            <span className={cn(text.muted, 'w-[8%] text-right tabular-nums')}>{r.quoted_candidates}/{r.candidate_count}</span>
            <span className={cn(text.muted, 'w-[17%] text-right tabular-nums')}>
              {r.unit_price_range ? `${money(r.unit_price_range.low)}–${money(r.unit_price_range.high)}` : L.mark_none}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
